import React, { useContext, useEffect } from 'react';
import * as R from 'ramda';
import { ActionBar } from 'common';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useUserRolePermissions } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import { CreateCustomerAuthContext } from '../Create';
import CustomerInformationAccordion from '../../components/CustomerInformationAccordion';
import ProductsAccordion from '../../components/ProductsAccordion';
import TerminalsAccordion from '../../components/TerminalsAccordion';
import DestinationsAccordion from '../../components/DestinationsAccordion';
import CarriersAccordion from '../../components/CarriersAccordion';
import { useCreateCustomerAuthorizationMutation } from '../../customerAuthorizationsEndpoints';
import TitleBar from '../TitleBarConnector';

const stackStyles = {
  /* eslint-disable sort-keys-fix/sort-keys-fix */
  mx: 'auto',
  pt: { xs: 6, lg: 9 },
  pb: 10,
  width: { xs: 1, lg: 760 },
};

// prettier-ignore
// pickControlNumbers :: ControlNumber -> ControlNumber
const pickControlNumbers = R.pick([
  'destinationIdName', 
  'idName',
  'itn'
]);

// pickProducts :: Product -> Product
const pickProducts = R.pick([
  'authorizationType',
  'authorizationVolume',
  'productId',
  'renewalVolume',
]);

const transformations = {
  carriers: R.map(R.prop('carrierId')),
  controlNumbers: R.map(pickControlNumbers),
  customerClass: R.prop('customerClassId'),
  products: R.map(pickProducts),
  terminals: R.map(R.prop('terminalId')),
};

// prettier-ignore
// getBody :: a -> CustomerAuthorization
const getBody = R.compose(
  R.omit(['customerClass', 'selection']),
  R.converge(R.assoc('controlClassId'), [
    R.prop('customerClass'), 
    R.identity
  ]),
  R.evolve(transformations)
);

const Summary = () => {
  const navigate = useNavigate();
  const { info, setIsNavHidden } = useContext(CreateCustomerAuthContext);
  const {
    authorizationType,
    terminals,
    products,
    controlNumbers,
    controlType,
    carriers,
    isCarrierRestricted,
  } = info;

  const dispatch = useDispatch();

  // prettier-ignore
  const { currentAffiliate: { 
    customerIdName: companyCustomerIdName, 
    dataCompanyId } = {} 
  } = useUserRolePermissions();

  const body = {
    ...getBody(info),
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  };
  // prettier-ignore
  const [submit, { 
    isError, 
    isLoading, 
    isSuccess 
  }] = useCreateCustomerAuthorizationMutation();

  const handleSubmit = () => {
    submit({ body })
      .then(res => {
        if (res.error) {
          // eslint-disable-next-line no-unused-vars
          Object.entries(res.error.data.errors).forEach(([key, messages]) => {
            [[messages], res.error.data.title].forEach(message => {
              dispatch(
                createSnack({
                  message: [message],
                  options: { variant: 'error' },
                })
              );
            });
          });
        } else {
          if (res.data.failureMessages && !R.isEmpty(res.data.failureMessages)) {
            [res.data.failureMessages].forEach(message => {
              dispatch(
                createSnack({
                  message,
                  options: { autoHideDuration: 4000 },
                })
              );
            });
          }
          if (res.data.message) {
            dispatch(
              createSnack({
                message: res.data.message,
                options: { autoHideDuration: 4000 },
              })
            );
          }
        }
      })
      .finally(() => {
        setIsNavHidden(false);
        navigate('../../');
      });
  };

  useEffect(() => {
    if (isError || isSuccess) {
      setIsNavHidden(false);
      navigate('../../');
    }
  }, [isError, isSuccess, navigate, setIsNavHidden]);

  return (
    <>
      <TitleBar />

      <Stack spacing={2} sx={stackStyles}>
        <Box>
          <CustomerInformationAccordion customerAuthorization={{ ...info }} defaultExpanded />
        </Box>
        <Box>
          <TerminalsAccordion controlType={controlType} defaultExpanded terminals={terminals} />
        </Box>
        <Box>
          <ProductsAccordion
            authorizationType={authorizationType}
            defaultExpanded
            products={products}
          />
        </Box>
        <Box>
          <DestinationsAccordion defaultExpanded destinations={controlNumbers} showItn />
        </Box>
        <Box>
          <CarriersAccordion
            carriers={carriers}
            defaultExpanded
            isCarrierRestricted={isCarrierRestricted}
          />
        </Box>
      </Stack>

      <ActionBar
        backLink="../carriers"
        continueLabel="Submit"
        disableAction={false}
        onContinue={handleSubmit}
      />

      <Backdrop open={isLoading} sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default Summary;
